import React, { useState, useEffect, useMemo } from 'react';
import '../../styles/common.css';
import '../../styles/test-plans.css';
import axiosInstance from '../../api/axiosInstance';
import CICDIntegrationModal from '../common/CICDIntegrationModal';
import { AccessibleSelect } from '../common';
import {
  MAX_TEST_EXECUTION_NAME_LENGTH,
  validateTestExecutionName,
} from './testExecutionValidation';
import { formatDate as formatUserDate } from '../../utils/dateTimeFormat';

const STATUS_OPTIONS = [
  // { value: 'draft', label: 'Draft' },
  // { value: 'active', label: 'Active' },
  { value: 'completed', label: 'Completed' },
  { value: 'archived', label: 'Archived' },
];

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
];

const resolveAssigneeId = (assignee, members, assigneeId) => {
  if (assigneeId) return String(assigneeId);
  if (!assignee || assignee === 'Unassigned') return '';
  const asNumber = Number(assignee);
  if (!Number.isNaN(asNumber) && members.some((member) => member.user_id === asNumber)) {
    return String(asNumber);
  }
  const byName = members.find((member) => member.name === assignee);
  if (byName) return String(byName.user_id);
  return '';
};

const getAssigneeName = (assignee, members, assigneeId) => {
  const resolvedId = assigneeId || assignee;
  if (!resolvedId || resolvedId === 'Unassigned') return 'Unassigned';
  const asNumber = Number(resolvedId);
  if (!Number.isNaN(asNumber)) {
    const member = members.find((m) => m.user_id === asNumber);
    if (member) return member.name;
  }
  return assignee || 'Unassigned';
};

const ViewTestPlanModal = ({ 
  testPlan, 
  onClose, 
  onEdit, 
  onDelete, 
  onGenerateScenarios, 
  onPushToFramework,
  mode = 'view',
  projectId,
  initialMembers = [],
  variant = 'modal',
}) => {
  const [isEditing, setIsEditing] = useState(mode === 'edit');
  const [editedPlan, setEditedPlan] = useState({ ...testPlan });
  const [members, setMembers] = useState(initialMembers);
  const [membersLoading, setMembersLoading] = useState(initialMembers.length === 0);
  const [errors, setErrors] = useState({});
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [scenarioCount, setScenarioCount] = useState(5);
  const [scenarioOptions, setScenarioOptions] = useState({
    includeEdgeCases: true,
    includeNegativeTests: true,
    includePerformanceTests: false
  });
  const [isCICDModalOpen, setIsCICDModalOpen] = useState(false);
  const [isCompleteConfirmOpen, setIsCompleteConfirmOpen] = useState(false);

  const assigneeOptions = useMemo(
    () =>
      members.map((member) => ({
        value: String(member.user_id),
        label: member.name,
      })),
    [members]
  );

  useEffect(() => {
    setMembers(initialMembers);
    setMembersLoading(initialMembers.length === 0);
  }, [initialMembers]);

  useEffect(() => {
    if (!projectId || initialMembers.length > 0) return;

    const fetchMembers = async () => {
      try {
        setMembersLoading(true);
        const response = await axiosInstance.get(
          `/projects/list_project_members?project_id=${projectId}`
        );

        if (response.data && response.status === 200) {
          setMembers(response.data || []);
        } else {
          setMembers([]);
        }
      } catch (error) {
        console.error('Error fetching project members:', error);
        setMembers([]);
      } finally {
        setMembersLoading(false);
      }
    };

    fetchMembers();
  }, [projectId, initialMembers.length]);

  useEffect(() => {
    setEditedPlan({
      ...testPlan,
      assignee: resolveAssigneeId(testPlan.assignee, members, testPlan.assignee_id),
    });
    setIsEditing(mode === 'edit');
  }, [testPlan, mode, members]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    handleFieldChange(name, value);
  };

  const handleFieldChange = (name, value) => {
    setEditedPlan((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  const handleScenarioOptionChange = (option) => {
    setScenarioOptions(prev => ({
      ...prev,
      [option]: !prev[option]
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    const nameError = validateTestExecutionName(editedPlan.name);
    if (nameError) {
      newErrors.name = nameError;
    }

    if (!editedPlan.description?.trim()) {
      newErrors.description = 'Description is required';
    }

    if (!editedPlan.assignee) {
      newErrors.assignee = 'Assignee is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    if (editedPlan.status === 'completed') {
      setIsCompleteConfirmOpen(true);
      return;
    }

    await performSave();
  };
  

  const performSave = async () => {
    try {
      await onEdit(editedPlan);
      setIsEditing(false);
      onClose();
    } catch (error) {
      if (error?.fieldErrors) {
        setErrors((prev) => ({ ...prev, ...error.fieldErrors }));
      }
    }
  };

  const handleConfirmComplete = async () => {
    setIsCompleteConfirmOpen(false);
    await performSave();
  };

  const handleCancelEdit = () => {
    setEditedPlan({
      ...testPlan,
      assignee: resolveAssigneeId(testPlan.assignee, members, testPlan.assignee_id),
    });
    onClose();
    // setIsEditing(false);
    setErrors({});
  };

  const handleDelete = () => {
    onDelete();
    onClose();
  };

  const handleGenerateScenarios = () => {
    const options = {
      count: scenarioCount,
      ...scenarioOptions
    };
    onGenerateScenarios(testPlan.id, options);
    onClose();
  };

  const formatDate = (dateString) => formatUserDate(dateString, 'N/A');

  const getStatusClass = (status) => {
    if (!status) return 'bg-gray-100 text-gray-800';
    switch(status.toLowerCase()) {
      case 'active':
      case 'in progress':
        return 'bg-green-100 text-green-800';
      case 'completed':
      case 'done':
        return 'bg-blue-100 text-blue-800';
      case 'planned':
      case 'to do':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityClass = (priority) => {
    if (!priority) return 'bg-gray-100 text-gray-800';
    switch(priority.toLowerCase()) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Render different content based on mode
  const renderContent = () => {
    // if (isDeleteConfirmOpen) {
    //   return (
    //     // <div className="text-center py-8">
    //     //   <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
    //     //     <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    //     //       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
    //     //     </svg>
    //     //   </div>
    //     //   <h3 className="text-lg font-semibold text-gray-900 mb-2">Confirm Deletion</h3>
    //     //   <p className="text-gray-600 mb-6">
    //     //     Are you sure you want to delete the test plan "<strong className="font-semibold">{testPlan.name}</strong>"? 
    //     //     This action cannot be undone.
    //     //   </p>
    //     //   <div className="flex justify-center gap-3">
    //     //     <button 
    //     //       className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
    //     //       onClick={() => setIsDeleteConfirmOpen(false)}
    //     //     >
    //     //       Cancel
    //     //     </button>
    //     //     <button 
    //     //       className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
    //     //       onClick={handleDelete}
    //     //     >
    //     //       Delete Test Plan
    //     //     </button>
    //     //   </div>
    //     // </div>
    //   );
    // }

    if (isEditing) {
      return (
        <div className="space-y-6">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
              Test Execution Name *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={editedPlan.name || ''}
              onChange={handleChange}
              maxLength={MAX_TEST_EXECUTION_NAME_LENGTH}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.name ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Enter test execution name"
            />
            {errors.name ? (
              <p className="mt-1 text-sm text-red-600">{errors.name}</p>
            ) : (
              <p className="mt-1 text-sm text-gray-500">
                Maximum {MAX_TEST_EXECUTION_NAME_LENGTH} characters allowed.
              </p>
            )}
          </div>
          
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
              Description *
            </label>
            <textarea
              id="description"
              name="description"
              value={editedPlan.description || ''}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.description ? 'border-red-500' : 'border-gray-300'
              }`}
              rows="4"
              placeholder="Enter test execution description"
            />
            {errors.description && <p className="mt-1 text-sm text-red-600">{errors.description}</p>}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label
                id="edit-status-label"
                htmlFor="status"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Status
              </label>
              <AccessibleSelect
                id="status"
                value={editedPlan.status || ''}
                onChange={(value) => handleFieldChange('status', value)}
                options={STATUS_OPTIONS}
                placeholder="Select Status"
                aria-labelledby="edit-status-label"
              />
            </div>
            
            <div>
              <label
                id="edit-priority-label"
                htmlFor="priority"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Priority
              </label>
              <AccessibleSelect
                id="priority"
                value={editedPlan.priority || 'medium'}
                onChange={(value) => handleFieldChange('priority', value)}
                options={PRIORITY_OPTIONS}
                placeholder="Select Priority"
                aria-labelledby="edit-priority-label"
              />
            </div>
          </div>
          
          <div>
            <label
              id="edit-assignee-label"
              htmlFor="assignee"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Assignee *
            </label>
            <AccessibleSelect
              id="assignee"
              value={editedPlan.assignee || ''}
              onChange={(value) => handleFieldChange('assignee', value)}
              options={assigneeOptions}
              placeholder={membersLoading ? 'Loading...' : 'Select Assignee'}
              disabled={membersLoading}
              required
              error={Boolean(errors.assignee)}
              aria-labelledby="edit-assignee-label"
            />
            {errors.assignee && (
              <div className="text-red-500 text-sm mt-1">{errors.assignee}</div>
            )}
          </div>
        </div>
      );
    }

    // View Mode
    return (
      <div className="space-y-6">
        {/* Test Plan Information */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b">Test Execution Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3 md:col-span-2">
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-gray-600 w-24 shrink-0">Status:</span>
                <span className={`px-3 py-1 rounded-full text-xs font-medium w-fit ${getStatusClass(testPlan.status)}`}>
                  {testPlan.status ? testPlan.status.charAt(0).toUpperCase() + testPlan.status.slice(1) : 'Draft'}
                </span>
              </div>
              
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-gray-600 w-24 shrink-0">Priority:</span>
                <span className={`px-3 py-1 rounded-full text-xs font-medium w-fit ${getPriorityClass(testPlan.priority)}`}>
                  {testPlan.priority || 'Medium'}
                </span>
              </div>
              
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-gray-600 w-24 shrink-0">Assignee:</span>
                <span className="text-sm text-gray-900">{getAssigneeName(testPlan.assignee, members, testPlan.assignee_id)}</span>
              </div>
              
              <div className="flex items-start gap-3">
                <span className="text-sm font-medium text-gray-600 w-24 shrink-0 pt-1">Description:</span>
                <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md min-h-[80px] flex-1">
                  {testPlan.description || 'No description provided'}
                </p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Linked Test Suites */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b">Linked Test Suites</h3>
          {testPlan.testSuites && testPlan.testSuites.length > 0 ? (
            <div className="space-y-2">
              {testPlan.testSuites.map((suite, index) => (
                <div key={suite.suite_id || index} className="flex justify-between items-center p-3 bg-gray-50 rounded-md">
                  <span className="text-sm font-medium text-gray-900">{suite.suite_name || `Suite ${index + 1}`}</span>
                  <span className="text-xs text-gray-500">ID: {suite.suite_id}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-4">
              <svg className="w-12 h-12 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-gray-600 mt-2">No test suites linked to this test Execution.</p>
            </div>
          )}
        </div>
        
        {/* Timeline */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b">Timeline</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex  items-center">
              <span className="text-m  text-black font-bold">Created:</span>
              <span className="text-m text-gray-900 ">{formatDate(testPlan.created_at)}</span>
            </div>
            <div className="flex  items-center">
              <span className="text-sm font-bold text-black">Last Updated:</span>
              <span className="text-sm mL-15px text-gray-900">{formatDate(testPlan.updated_at)}</span>
            </div>
          </div>
        </div>
        
        {/* Test Scenario Generation - Only in view mode */}
        <div>
          {/* <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b">Test Scenario Generation</h3> */}
          
          {/* Scenario Count Selection */}
          {/* <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-3">Number of scenarios:</label>
            <div className="flex flex-wrap gap-2">
              {[5, 10, 20].map((count) => (
                <button
                  key={count}
                  className={`px-4 py-2 rounded-md border transition-colors ${
                    scenarioCount === count
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                  onClick={() => setScenarioCount(count)}
                >
                  {count} Scenarios
                </button>
              ))}
            </div>
          </div> */}
          
          {/* Scenario Options */}
          <div className="mb-6">
             {/* <label className="block text-sm font-medium text-gray-700 mb-3">Include additional test types:</label> */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
           
              {/* Edge Cases Option */}
              {/* <label className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={scenarioOptions.includeEdgeCases}
                    onChange={() => handleScenarioOptionChange('includeEdgeCases')}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                </div>
                <div className="flex flex-col">
                  <span className="text-sm font-medium text-gray-700">Edge Cases</span>
                  <span className="text-xs text-gray-500">Boundary conditions</span>
                </div>
              </label> */}
              
              {/* Negative Tests Option */}
              {/* <label className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={scenarioOptions.includeNegativeTests}
                    onChange={() => handleScenarioOptionChange('includeNegativeTests')}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                </div>
                <div className="flex flex-col">
                  <span className="text-sm font-medium text-gray-700">Negative Tests</span>
                  <span className="text-xs text-gray-500">Invalid inputs</span>
                </div>
              </label> */}
              
              {/* Performance Tests Option */}
              {/* <label className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={scenarioOptions.includePerformanceTests}
                    onChange={() => handleScenarioOptionChange('includePerformanceTests')}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                </div>
                <div className="flex flex-col">
                  <span className="text-sm font-medium text-gray-700">Performance Tests</span>
                  <span className="text-xs text-gray-500">Load & stress</span>
                </div>
              </label> */}
            </div>
          </div>
          
          {/* Generate Button */}
          {/* <div className="flex justify-center">
            <button 
              className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium"
              onClick={handleGenerateScenarios}
            >
              Generate {scenarioCount} Scenarios
            </button>
          </div> */}
        </div>
      </div>
    );
  };

  const renderFooter = () => {
    if (isDeleteConfirmOpen) {
      return null;
    }

    if (isEditing) {
      return (
        <div className="flex justify-end gap-3">
          <button 
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
            onClick={handleCancelEdit}
          >
            Cancel
          </button>
          <button 
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            onClick={handleSave}
          >
            Save Changes
          </button>
        </div>
      );
    }

    // View mode footer
    return (
      <div className="flex justify-between items-center">
        <button 
          className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
          onClick={() => handleDelete()}
        >
          Delete
        </button>
        <div className="flex gap-3">
          <button 
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
            onClick={() => setIsEditing(true)}
          >
            Edit
          </button>
          <button 
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
            onClick={() => setIsCICDModalOpen(true)}
          >
            DevOps Integration
          </button>
        </div>
      </div>
    );
  };

  return (
    <>
      <div
        className={variant === 'page' ? 'rt-plan-as-page' : 'rt-plan-modal-overlay'}
        {...(variant === 'page'
          ? {}
          : {
              'data-rt-modal-overlay ': true,
              onClick: onClose,
            })}
      >
        <div
          className={variant === 'page' ? 'rt-plan-page-body' : 'rt-plan-modal-panel'}
          onClick={variant === 'page' ? undefined : (e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className={`rt-plan-modal-header${variant === 'page' ? ' rt-entity-chrome-hidden' : ''}`}>
            <h2>
              {isDeleteConfirmOpen ? 'Confirm Deletion' : 
               isEditing ? 'Edit Test Plan' : 
               testPlan.name || 'Test Plan Details'}
            </h2>
            {variant !== 'page' && (
              <button 
                type="button"
                className="rt-plan-modal-close"
                onClick={onClose}
                aria-label="Close"
              >
                ×
              </button>
            )}
          </div>
          
          {/* Body */}
          <div className="rt-plan-modal-body">
            {renderContent()}
          </div>
          
          {/* Footer */}
          <div className="rt-plan-modal-footer">
            {renderFooter()}
          </div>
        </div>
      </div>
      
      {/* CI/CD Integration Modal */}
      <CICDIntegrationModal
        isOpen={isCICDModalOpen}
        onClose={() => setIsCICDModalOpen(false)}
        testSuites={testPlan.testSuites || []}
        onPushToFramework={onPushToFramework}
      />

      {/* Completion Confirmation Modal */}
      {isCompleteConfirmOpen && (
        <div
          data-rt-modal-overlay
          className="rt-plan-modal-overlay"
          style={{ zIndex: 60 }}
          onClick={() => setIsCompleteConfirmOpen(false)}
        >
          <div className="rt-plan-modal-panel rt-plan-modal-panel--sm" onClick={(e) => e.stopPropagation()}>
            <h3 className="rt-plan-confirm-title">Confirm Completion</h3>
            <p className="rt-plan-confirm-body">
              Once this test execution is marked as <strong>Completed</strong>, you will not be able to link
              more test suites or edit the test plan.
            </p>
            <div className="rt-plan-confirm-actions">
              <button
                type="button"
                className="rt-btn rt-btn--secondary"
                onClick={() => setIsCompleteConfirmOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rt-btn rt-btn--primary"
                onClick={handleConfirmComplete}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ViewTestPlanModal;